import { useState, useEffect, useCallback } from 'react';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import { supabase } from '@/lib/supabase';
import type { DashboardFinancieroAdmin } from '@/types';
import DataTable from '@/components/DataTable';
import FilterBar, { Filter } from '@/components/FilterBar';
import FinancialChart from '@/components/FinancialChart';
import toast from 'react-hot-toast';

/**
 * ADMIN GLOBAL FINANCIAL DASHBOARD
 * 
 * Features:
 * - Aggregated financial data across ALL clients
 * - AI-generated risk alerts and commentary
 * - Income vs Expenses evolution charts
 * - Client risk heatmap
 * - Drill-down to individual client details
 */
export default function AdminPanelFinancieroCompleto() {
  const { user, loading } = useAuth();
  const router = useRouter();
  
  const [dashboardData, setDashboardData] = useState<DashboardFinancieroAdmin | null>(null);
  const [filtros, setFiltros] = useState<Record<string, any>>({});
  const [clienteSeleccionado, setClienteSeleccionado] = useState<any>(null);
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState<string>('2025');
  const [comentarioIA, setComentarioIA] = useState<string>('');
  const [distribucionGastosIA, setDistribucionGastosIA] = useState<{categoria: string; importe: number; porcentaje: number}[]>([]);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoadingData(true);

      // Query datos_financieros (real table) filtered by year
      const { data: financialData, error: financialError } = await supabase
        .from('datos_financieros')
        .select('*, clientes(razon_social, nombre_comercial, nif)')
        .like('periodo', `${periodoSeleccionado}-%`);

      if (financialError) throw financialError;

      if (!financialData || financialData.length === 0) {
        setDashboardData({
          totalIngresos: 0,
          totalGastos: 0,
          resultadoGlobal: 0,
          margenPromedio: 0,
          clientesEnRiesgo: 0,
          datosClientes: [],
          evolucionMensual: [],
          distribucionGastos: []
        });
        return;
      }

      // Aggregate monthly rows per client
      const clienteMap: Record<string, {
        clienteId: string;
        nombreCliente: string;
        ingresos: number;
        gastos: number;
        resultado: number;
        nivelRiesgo: 'bajo' | 'medio' | 'alto';
        ultimoCierre?: Date;
      }> = {};

      const riesgoOrder = { bajo: 0, medio: 1, alto: 2 } as const;

      financialData.forEach(row => {
        if (!clienteMap[row.cliente_id]) {
          clienteMap[row.cliente_id] = {
            clienteId: row.cliente_id,
            nombreCliente: (row.clientes as any)?.nombre_comercial || (row.clientes as any)?.razon_social || 'Cliente sin nombre',
            ingresos: 0,
            gastos: 0,
            resultado: 0,
            nivelRiesgo: 'bajo',
            ultimoCierre: row.ultimo_cierre ? new Date(row.ultimo_cierre) : undefined,
          };
        }
        clienteMap[row.cliente_id].ingresos += parseFloat(row.ingresos) || 0;
        clienteMap[row.cliente_id].gastos += parseFloat(row.gastos) || 0;
        clienteMap[row.cliente_id].resultado += parseFloat(row.resultado) || 0;
        // Track worst risk level seen for this client
        const rowRiesgo = (row.riesgo_ia || 'bajo') as 'bajo' | 'medio' | 'alto';
        if (riesgoOrder[rowRiesgo] > riesgoOrder[clienteMap[row.cliente_id].nivelRiesgo]) {
          clienteMap[row.cliente_id].nivelRiesgo = rowRiesgo;
        }
      });

      const datosClientes = Object.values(clienteMap).map(c => ({
        ...c,
        margen: c.ingresos > 0 ? (c.resultado / c.ingresos) * 100 : 0,
      }));

      const totalIngresos = datosClientes.reduce((sum, c) => sum + c.ingresos, 0);
      const totalGastos = datosClientes.reduce((sum, c) => sum + c.gastos, 0);
      const resultadoGlobal = totalIngresos - totalGastos;
      const margenPromedio = totalIngresos > 0 ? (resultadoGlobal / totalIngresos) * 100 : 0;
      const clientesEnRiesgo = datosClientes.filter(c => c.nivelRiesgo === 'alto').length;

      // Monthly evolution — aggregate all clients per period
      const mesLabels: Record<string, string> = {
        '01':'Ene','02':'Feb','03':'Mar','04':'Abr','05':'May','06':'Jun',
        '07':'Jul','08':'Ago','09':'Sep','10':'Oct','11':'Nov','12':'Dic'
      };
      const mesMap: Record<string, { ingresos: number; gastos: number; resultado: number }> = {};
      financialData.forEach(row => {
        const key = row.periodo as string; // YYYY-MM
        if (!mesMap[key]) mesMap[key] = { ingresos: 0, gastos: 0, resultado: 0 };
        mesMap[key].ingresos += parseFloat(row.ingresos) || 0;
        mesMap[key].gastos += parseFloat(row.gastos) || 0;
        mesMap[key].resultado += parseFloat(row.resultado) || 0;
      });
      const evolucionMensual = Object.entries(mesMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([periodo, data]) => ({
          mes: mesLabels[periodo.split('-')[1]] || periodo,
          ...data,
        }));

      // Expense distribution will come from AI insights (loaded separately)
      const distribucionGastos = distribucionGastosIA;

      setDashboardData({
        totalIngresos,
        totalGastos,
        resultadoGlobal,
        margenPromedio,
        clientesEnRiesgo,
        datosClientes,
        evolucionMensual,
        distribucionGastos,
      });

    } catch (error: any) {
      console.error('Error loading dashboard data:', error);
      toast.error('Error al cargar datos financieros');
    } finally {
      setLoadingData(false);
    }
  }, [periodoSeleccionado]);

  // Load AI insights from Supabase (cached, refreshed 2x/day)
  useEffect(() => {
    const loadAIInsights = async () => {
      try {
        const { data, error } = await supabase
          .from('ai_dashboard_insights')
          .select('*')
          .eq('scope', 'global')
          .eq('activo', true)
          .order('generado_en', { ascending: false });

        if (error || !data || data.length === 0) return;

        // AI commentary
        const comentario = data.find(d => d.tipo === 'comentario_financiero');
        if (comentario) setComentarioIA(comentario.contenido);

        // Expense distribution
        const distRow = data.find(d => d.tipo === 'distribucion_gastos');
        if (distRow && distRow.metadata?.categorias) {
          const totalGastos = dashboardData?.totalGastos || 0;
          const cats = (distRow.metadata.categorias as any[]).map(c => ({
            categoria: c.categoria,
            porcentaje: c.porcentaje,
            importe: totalGastos * (c.porcentaje / 100),
          }));
          setDistribucionGastosIA(cats);
        }
      } catch (err) {
        console.error('Error loading AI insights:', err);
      }
    };
    if (dashboardData) loadAIInsights();
  }, [dashboardData]);

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'admin')) {
      router.push('/');
    }
    if (user && user.rol === 'admin') {
      loadDashboardData();
    }
  }, [user, loading, router, loadDashboardData]);

  const filterConfig: Filter[] = [
    {
      key: 'buscar',
      label: 'Buscar',
      type: 'text',
      placeholder: 'Razón social...',
    },
    {
      key: 'nivelRiesgo',
      label: 'Nivel de Riesgo',
      type: 'select',
      options: [
        { value: '', label: 'Todos' },
        { value: 'bajo', label: 'Bajo' },
        { value: 'medio', label: 'Medio' },
        { value: 'alto', label: 'Alto' },
      ],
    },
  ];

  const datosFiltrados = dashboardData?.datosClientes.filter(cliente => {
    if (filtros.buscar) {
      const busqueda = filtros.buscar.toLowerCase();
      if (!cliente.nombreCliente.toLowerCase().includes(busqueda)) {
        return false;
      }
    }
    if (filtros.nivelRiesgo && cliente.nivelRiesgo !== filtros.nivelRiesgo) {
      return false;
    }
    return true;
  }) || [];

  const handleFiltroChange = (key: string, value: any) => {
    setFiltros({ ...filtros, [key]: value });
  };

  const handleLimpiarFiltros = () => {
    setFiltros({});
  };

  const handleVerDetalle = (cliente: any) => {
    setClienteSeleccionado(cliente);
    setMostrarDetalle(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const getRiesgoBadge = (riesgo: 'bajo' | 'medio' | 'alto') => {
    const badges = {
      bajo: 'badge-success',
      medio: 'badge-warning',
      alto: 'badge-danger',
    };
    const labels = {
      bajo: <><span className="material-symbols-outlined text-sm align-middle">circle</span> Bajo</>,
      medio: <><span className="material-symbols-outlined text-sm align-middle">circle</span> Medio</>,
      alto: <><span className="material-symbols-outlined text-sm align-middle">circle</span> Alto</>,
    };
    return <span className={`badge ${badges[riesgo]}`}>{labels[riesgo]}</span>;
  };

  const columns = [
    {
      key: 'nombreCliente',
      header: 'CLIENTE',
      sortable: true,
      render: (cliente: any) => (
        <div>
          <div className="font-medium text-calm-text">{cliente.nombreCliente}</div>
          <div className="text-xs text-calm-muted">ID: {cliente.clienteId.substring(0, 8)}...</div>
        </div>
      ),
    },
    {
      key: 'ingresos',
      header: 'INGRESOS',
      sortable: true,
      render: (cliente: any) => (
        <div className="font-medium text-green-600">{formatCurrency(cliente.ingresos)}</div>
      ),
    },
    {
      key: 'gastos',
      header: 'GASTOS',
      sortable: true,
      render: (cliente: any) => (
        <div className="font-medium text-red-600">{formatCurrency(cliente.gastos)}</div>
      ),
    },
    {
      key: 'resultado',
      header: 'RESULTADO',
      sortable: true,
      render: (cliente: any) => (
        <div className={`font-light ${cliente.resultado >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {formatCurrency(cliente.resultado)}
        </div>
      ),
    },
    {
      key: 'margen',
      header: 'MARGEN %',
      sortable: true,
      render: (cliente: any) => (
        <div className={`font-medium ${cliente.margen >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {cliente.margen.toFixed(1)}%
        </div>
      ),
    },
    {
      key: 'nivelRiesgo',
      header: 'RIESGO IA',
      sortable: true,
      render: (cliente: any) => getRiesgoBadge(cliente.nivelRiesgo),
    },
  ];

  if (loading || loadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-calm-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-admin mx-auto"></div>
          <p className="mt-4 text-calm-muted">Cargando dashboard financiero...</p>
        </div>
      </div>
    );
  }

  // comentarioIA is now loaded from Supabase state (see useState above)

  return (
    <>
      <Head>
        <title>Panel Financiero Global - Admin - Externaliza2</title>
      </Head>
      <Layout rol="admin">
        <div className="h-[calc(100vh-72px)] overflow-y-auto space-y-6 p-4 md:p-8">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-5xl font-light font-display text-calm-text">Panel Financiero Global</h1>
              <p className="text-calm-muted mt-1">Análisis consolidado de todos los clientes con IA</p>
            </div>
            <div className="flex space-x-2">
              <select
                value={periodoSeleccionado}
                onChange={(e) => setPeriodoSeleccionado(e.target.value)}
                className="input"
              >
                <option value="2026">2026</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
              </select>
              <button onClick={loadDashboardData} className="ghost-btn flex items-center gap-2">
                <span className="material-symbols-outlined text-base">refresh</span> Actualizar
              </button>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
              <div className="text-sm text-green-700 font-medium">Ingresos Totales</div>
              <div className="text-3xl font-light text-green-900 mt-1">
                {formatCurrency(dashboardData?.totalIngresos || 0)}
              </div>
              <div className="text-xs text-calm-muted mt-1">
                Todos los clientes combinados
              </div>
            </div>
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
              <div className="text-sm text-red-700 font-medium">Gastos Totales</div>
              <div className="text-3xl font-light text-red-900 mt-1">
                {formatCurrency(dashboardData?.totalGastos || 0)}
              </div>
              <div className="text-xs text-calm-muted mt-1">
                Ratio: {dashboardData?.totalIngresos ? ((dashboardData.totalGastos / dashboardData.totalIngresos) * 100).toFixed(1) : 0}% de ingresos
              </div>
            </div>
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
              <div className="text-sm text-blue-700 font-medium">Resultado Global</div>
              <div className={`text-3xl font-light mt-1 ${(dashboardData?.resultadoGlobal || 0) >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                {formatCurrency(dashboardData?.resultadoGlobal || 0)}
              </div>
              <div className="text-xs text-calm-muted mt-1">
                Margen: {dashboardData?.margenPromedio.toFixed(1)}%
              </div>
            </div>
            <div className="bg-gradient-to-br from-white to-amber-50/20 border border-amber-200/40 rounded-huge p-8">
              <div className="text-sm text-yellow-700 font-medium">Clientes en Riesgo</div>
              <div className="text-3xl font-light text-yellow-900 mt-1">
                {dashboardData?.clientesEnRiesgo || 0}
              </div>
              <div className="text-xs text-calm-muted mt-1">
                {dashboardData?.datosClientes.length || 0} clientes totales
              </div>
            </div>
          </div>

          {/* AI Commentary */}
          <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
            <div className="flex items-start space-x-3">
              <div className="w-12 h-12 rounded-2xl bg-steel-azure-50 flex items-center justify-center text-steel-azure-500">
                <span className="material-symbols-outlined text-3xl">smart_toy</span>
              </div>
              <div className="flex-1">
                <h3 className="font-light text-calm-text mb-2">Comentario IA Automático</h3>
                <p className="text-sm text-calm-muted">{comentarioIA || 'Los insights se generan automáticamente 2 veces al día.'}</p>
              </div>
            </div>
          </div>

          {/* Management Summary - Resumen de Gestión */}
          {dashboardData && dashboardData.datosClientes.length > 0 && (() => {
            const empresasEnPerdidas = dashboardData.datosClientes.filter(c => c.resultado < 0);
            const riesgoBajo = dashboardData.datosClientes.filter(c => c.nivelRiesgo === 'bajo').length;
            const riesgoMedio = dashboardData.datosClientes.filter(c => c.nivelRiesgo === 'medio').length;
            const riesgoAlto = dashboardData.datosClientes.filter(c => c.nivelRiesgo === 'alto').length;
            const empresasMargenNegativo = dashboardData.datosClientes.filter(c => c.margen < 0);
            const alertas: string[] = [];
            if (empresasMargenNegativo.length > 0) alertas.push(`${empresasMargenNegativo.length} empresa(s) con margen negativo`);
            if (dashboardData.clientesEnRiesgo > 0) alertas.push(`${dashboardData.clientesEnRiesgo} cliente(s) requieren atención`);
            if (dashboardData.margenPromedio < 5) alertas.push(`Margen global bajo: ${dashboardData.margenPromedio.toFixed(1)}%`);

            return (
              <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
                <h3 className="text-lg font-light text-calm-text mb-6 flex items-center gap-2">
                  <span className="material-symbols-outlined text-steel-azure-500">assessment</span>
                  Resumen de Gestión
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Column 1: Empresas en Pérdidas */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-red-700">
                      <span className="material-symbols-outlined text-base">trending_down</span>
                      Empresas en Pérdidas
                    </div>
                    <div className="text-3xl font-light text-red-900">{empresasEnPerdidas.length}</div>
                    {empresasEnPerdidas.length > 0 ? (
                      <div className="space-y-1">
                        {empresasEnPerdidas.slice(0, 5).map((c, i) => (
                          <div key={i} className="text-xs text-calm-muted flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"></span>
                            {c.nombreCliente} ({formatCurrency(c.resultado)})
                          </div>
                        ))}
                        {empresasEnPerdidas.length > 5 && (
                          <div className="text-xs text-calm-muted italic">y {empresasEnPerdidas.length - 5} más...</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-green-600">Todas las empresas con resultados positivos</div>
                    )}
                  </div>

                  {/* Column 2: Distribución de Riesgo */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-steel-azure-700">
                      <span className="material-symbols-outlined text-base">shield</span>
                      Distribución de Riesgo
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="badge badge-success">{riesgoBajo} Bajo</span>
                        <div className="flex-1 mx-3 bg-calm-bg rounded-full h-2">
                          <div className="bg-green-400 h-2 rounded-full" style={{ width: `${dashboardData.datosClientes.length > 0 ? (riesgoBajo / dashboardData.datosClientes.length) * 100 : 0}%` }}></div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="badge badge-warning">{riesgoMedio} Medio</span>
                        <div className="flex-1 mx-3 bg-calm-bg rounded-full h-2">
                          <div className="bg-amber-400 h-2 rounded-full" style={{ width: `${dashboardData.datosClientes.length > 0 ? (riesgoMedio / dashboardData.datosClientes.length) * 100 : 0}%` }}></div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="badge badge-danger">{riesgoAlto} Alto</span>
                        <div className="flex-1 mx-3 bg-calm-bg rounded-full h-2">
                          <div className="bg-red-400 h-2 rounded-full" style={{ width: `${dashboardData.datosClientes.length > 0 ? (riesgoAlto / dashboardData.datosClientes.length) * 100 : 0}%` }}></div>
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-calm-muted">{dashboardData.datosClientes.length} empresas analizadas</div>
                  </div>

                  {/* Column 3: Alertas de Gestión */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-amber-700">
                      <span className="material-symbols-outlined text-base">flag</span>
                      Alertas de Gestión
                    </div>
                    {alertas.length > 0 ? (
                      <div className="space-y-2">
                        {alertas.map((alerta, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm text-calm-text">
                            <span className="material-symbols-outlined text-amber-500 text-base mt-0.5">warning</span>
                            {alerta}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-green-600">
                        <span className="material-symbols-outlined text-base">check_circle</span>
                        Sin alertas activas
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Income vs Expenses Evolution */}
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
              <h3 className="text-lg font-light text-calm-text mb-4">
                <span className="material-symbols-outlined text-base mr-1 align-middle">trending_up</span>
                Evolución Ingresos vs Gastos
              </h3>
              <FinancialChart
                type="line"
                data={{
                  labels: dashboardData?.evolucionMensual.map(d => d.mes) || [],
                  datasets: [
                    {
                      label: 'Ingresos',
                      data: dashboardData?.evolucionMensual.map(d => d.ingresos) || [],
                      borderColor: 'rgb(34, 197, 94)',
                      backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    },
                    {
                      label: 'Gastos',
                      data: dashboardData?.evolucionMensual.map(d => d.gastos) || [],
                      borderColor: 'rgb(239, 68, 68)',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    },
                  ],
                }}
              />
            </div>

            {/* Expense Distribution */}
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
              <h3 className="text-lg font-light text-calm-text mb-4">
                <span className="material-symbols-outlined text-base mr-1 align-middle">bar_chart</span>
                Distribución de Gastos
              </h3>
              <div className="space-y-3">
                {(distribucionGastosIA.length > 0 ? distribucionGastosIA : (dashboardData?.distribucionGastos || [])).slice(0, 5).map((cat, idx) => (
                  <div key={idx}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-calm-text">{cat.categoria}</span>
                      <span className="text-sm font-medium text-calm-text">{formatCurrency(cat.importe)}</span>
                    </div>
                    <div className="w-full bg-calm-bg rounded-full h-2">
                      <div 
                        className="bg-calm-accent h-2 rounded-full" 
                        style={{ width: `${cat.porcentaje}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-calm-muted mt-1">{cat.porcentaje.toFixed(1)}% del total</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Clients Table */}
          <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-light text-calm-text">
                <span className="material-symbols-outlined text-base mr-1 align-middle">group</span>
                Detalle por Cliente
              </h2>
              <span className="text-sm text-calm-muted">
                {datosFiltrados.length} de {dashboardData?.datosClientes.length || 0} clientes
              </span>
            </div>

            <FilterBar
              filters={filterConfig}
              values={filtros}
              onChange={handleFiltroChange}
              onClear={handleLimpiarFiltros}
            />

            <DataTable
              data={datosFiltrados}
              columns={columns}
              onRowClick={handleVerDetalle}
              actions={(cliente) => (
                <button
                  onClick={() => handleVerDetalle(cliente)}
                  className="text-calm-accent hover:text-calm-accent/80"
                  title="Ver detalle"
                >
                  <span className="material-symbols-outlined">bar_chart</span>
                </button>
              )}
            />
          </div>

          {/* Client Detail Modal */}
          {mostrarDetalle && clienteSeleccionado && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-huge shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-calm-border flex justify-between items-center sticky top-0 bg-white z-10">
                  <div>
                    <h2 className="text-2xl font-light text-calm-text">{clienteSeleccionado.nombreCliente}</h2>
                    <p className="text-calm-muted">Periodo: {periodoSeleccionado}</p>
                  </div>
                  <button
                    onClick={() => {
                      setMostrarDetalle(false);
                      setClienteSeleccionado(null);
                    }}
                    className="ghost-btn text-2xl"
                  >
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
                      <div className="text-sm text-green-700">Ingresos</div>
                      <div className="text-2xl font-light text-green-900 mt-1">
                        {formatCurrency(clienteSeleccionado.ingresos)}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
                      <div className="text-sm text-red-700">Gastos</div>
                      <div className="text-2xl font-light text-red-900 mt-1">
                        {formatCurrency(clienteSeleccionado.gastos)}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
                      <div className="text-sm text-blue-700">Resultado</div>
                      <div className={`text-2xl font-light mt-1 ${clienteSeleccionado.resultado >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                        {formatCurrency(clienteSeleccionado.resultado)}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
                      <div className="text-sm text-blue-700">Margen</div>
                      <div className={`text-2xl font-light mt-1 ${clienteSeleccionado.margen >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                        {clienteSeleccionado.margen.toFixed(1)}%
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
                    <h3 className="text-lg font-light text-calm-text mb-3">
                      <span className="material-symbols-outlined text-base mr-1 align-middle">gps_fixed</span>
                      Análisis de Riesgo IA
                    </h3>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-calm-text">Nivel de Riesgo:</span>
                      {getRiesgoBadge(clienteSeleccionado.nivelRiesgo)}
                    </div>
                    <div className="p-4 bg-calm-bg rounded-huge">
                      <p className="text-sm text-calm-text">
                        {clienteSeleccionado.nivelRiesgo === 'alto' && 
                          'ALERTA: La IA ha detectado señales de riesgo alto. Se recomienda revisión urgente de la situación financiera, análisis de liquidez y seguimiento cercano. Considerar plan de acción inmediato.'}
                        {clienteSeleccionado.nivelRiesgo === 'medio' && 
                          'PRECAUCIÓN: La IA ha identificado algunos indicadores de riesgo. Se sugiere monitoreo regular de la evolución financiera, revisión de gastos y análisis de tendencias.'}
                        {clienteSeleccionado.nivelRiesgo === 'bajo' && 
                          'ESTABLE: La situación financiera es estable según el análisis IA. Los indicadores principales están en niveles saludables. Continuar con el seguimiento habitual y mantener las prácticas actuales.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Layout>
    </>
  );
}

// generateAICommentary removed — now loaded from ai_dashboard_insights table
// AI insights are generated 2x/day via /api/ai-insights/refresh cron endpoint
